import React, { useState, useEffect, useRef } from 'react';
import { Ship, Maximize2, Plus, Minus, Activity } from 'lucide-react';
import { WorldMap } from '@/components/ui/WorldMap';

const STATUS = [
  { key: 'ontime',  label: 'ON TIME',    count: 5, dot: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-500/20 dark:ring-emerald-400/20', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  { key: 'delayed', label: 'DELAYED',    count: 3, dot: 'bg-rose-500',    text: 'text-rose-600 dark:text-rose-400',    ring: 'ring-rose-500/20 dark:ring-rose-400/20', bg: 'bg-rose-50 dark:bg-rose-950/30' },
  { key: 'transit', label: 'IN TRANSIT', count: 2, dot: 'bg-amber-500',   text: 'text-amber-600 dark:text-amber-400',   ring: 'ring-amber-500/20 dark:ring-amber-400/20', bg: 'bg-amber-50 dark:bg-amber-950/30' },
];

// Live shipping routes between major global ports
const VESSEL_ROUTES = [
  {
    start: { lat: 31.2304,  lng: 121.4737, label: 'Shanghai' },
    end:   { lat: 34.0522,  lng: -118.2437, label: 'Los Angeles' },
  },
  {
    start: { lat: 51.9244,  lng: 4.4777,   label: 'Rotterdam' },
    end:   { lat: 40.7128,  lng: -74.006,  label: 'New York' },
  },
  {
    start: { lat: 1.3521,   lng: 103.8198, label: 'Singapore' },
    end:   { lat: 51.9244,  lng: 4.4777,   label: 'Rotterdam' },
  },
  {
    start: { lat: 19.076,   lng: 72.8777,  label: 'Mumbai' },
    end:   { lat: 25.2048,  lng: 55.2708,  label: 'Dubai' },
  },
  {
    start: { lat: 35.6762,  lng: 139.6503, label: 'Tokyo' },
    end:   { lat: 47.6062,  lng: -122.3321, label: 'Seattle' },
  },
  {
    start: { lat: 35.1796,  lng: 129.0756, label: 'Busan' },
    end:   { lat: 1.3521,   lng: 103.8198, label: 'Singapore' },
  },
  {
    start: { lat: 53.5753,  lng: 10.0153,  label: 'Hamburg' },
    end:   { lat: 31.2304,  lng: 121.4737, label: 'Shanghai' },
  },
];

export default function VesselMap() {
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const mapContainerRef = useRef(null);

  // Detect dark mode from DOM
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setDarkMode(isDark);
    };

    // Initial check
    checkDarkMode();

    // Listen for theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleFullscreen = () => {
    if (!mapContainerRef.current) return;
    
    if (!isFullscreen) {
      if (mapContainerRef.current.requestFullscreen) {
        mapContainerRef.current.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  return (
    <section className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 dark:from-slate-600 dark:to-slate-800 text-white flex items-center justify-center shadow-md">
            <Ship className="w-5 h-5" />
          </div>
          <div>
            <div className="text-[14px] font-semibold text-slate-900 dark:text-white">Live Vessel Tracking</div>
            <div className="text-[12px] text-slate-500 dark:text-slate-400">10 vessels · 17,032 containers</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {STATUS.map((s) => (
            <div
              key={s.key}
              className={`inline-flex items-center gap-2 px-3 h-8 rounded-lg border ${s.bg} ${s.ring} ring-1 transition-all duration-200`}
            >
              <span className={`w-2 h-2 rounded-full ${s.dot} shadow-sm`} />
              <span className={`text-[10.5px] font-bold tracking-wider ${s.text}`}>{s.label}</span>
              <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-300 tabular-nums">{s.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Map Container */}
      <div 
        ref={mapContainerRef}
        className={`relative h-[420px] transition-all duration-300 ${
          isFullscreen ? 'fixed inset-0 z-50 h-screen' : ''
        }`}
      >
        {/* Map Background */}
        <div className={`absolute inset-0 ${
          darkMode
            ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
            : 'bg-gradient-to-br from-sky-50 via-blue-50 to-indigo-50'
        }`} />
        
        {/* World Map Component */}
        <div 
          className="absolute inset-0 transition-transform duration-300"
          style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }}
        >
          <WorldMap
            dots={VESSEL_ROUTES}
            lineColor={darkMode ? "#60A5FA" : "#3B82F6"}
            showLabels={true}
            animationDuration={2}
            loop={true}
            isDark={darkMode}
          />
        </div>

        {/* Zoom Controls */}
        <div className={`absolute top-4 right-4 flex flex-col gap-1 rounded-xl backdrop-blur-sm border p-1 z-10 ${
          darkMode 
            ? 'bg-slate-900/80 border-slate-700 shadow-lg' 
            : 'bg-white/90 border-slate-200 shadow-md'
        }`}>
          <button 
            onClick={handleZoomIn}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
              darkMode 
                ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Plus className="w-4 h-4" />
          </button>
          <div className={`w-6 h-px mx-auto ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />
          <button 
            onClick={handleZoomOut}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
              darkMode 
                ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Minus className="w-4 h-4" />
          </button>
          <div className={`w-6 h-px mx-auto ${darkMode ? 'bg-slate-700' : 'bg-slate-200'}`} />
          <button 
            onClick={handleFullscreen}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
              darkMode 
                ? 'text-slate-300 hover:bg-slate-800 hover:text-white' 
                : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>

        {/* Legend Bottom Left */}
        <div className={`absolute bottom-4 left-4 flex items-center gap-3 px-3 py-1.5 rounded-full
                        backdrop-blur-sm border text-[10.5px] z-10 ${
                          darkMode 
                            ? 'bg-slate-900/80 border-slate-700 text-slate-300' 
                            : 'bg-white/90 border-slate-200 text-slate-600'
                        }`}>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm" /> On Time
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-rose-500 shadow-sm" /> Delayed
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-amber-500 shadow-sm" /> In Transit
          </span>
        </div>

        {/* Live Status Indicator */}
        <div className={`absolute bottom-4 right-4 flex items-center gap-2 px-3 py-1.5 rounded-full
                        backdrop-blur-sm border text-[10.5px] z-10 ${
                          darkMode 
                            ? 'bg-emerald-950/80 border-emerald-800/50 text-emerald-400' 
                            : 'bg-emerald-50/90 border-emerald-200 text-emerald-700'
                        }`}>
          <Activity className="w-3 h-3 animate-pulse" />
          <span className="font-medium">Live Tracking Active</span>
        </div>
      </div>
    </section>
  );
}